'use client';

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ROUTES } from '@/lib/routes';

const READY_TIMEOUT_MS = 1000;

interface RouteReadyValue {
  readyPath: string | null;
  readySeq: number;
  markRouteReady: (path: string) => void;
  heldPathRef: MutableRefObject<string | null>;
}

const RouteReadyContext = createContext<RouteReadyValue>({
  readyPath: null,
  readySeq: 0,
  markRouteReady: () => {},
  heldPathRef: { current: null },
});

const NavigationContext = createContext<{ exit: (href: string) => void }>({
  exit: (href) => {
    if (typeof window !== 'undefined') window.location.href = href;
  },
});

// Routes enter as soon as they mount. A route that needs to finish setting up
// first calls this hook, then calls the returned function once it is ready.
// Child layout effects run before SwipeTransition's, so the hold is in place in time.
export function useHoldRouteEntry(path: string) {
  const { heldPathRef, markRouteReady } = useContext(RouteReadyContext);
  useLayoutEffect(() => {
    heldPathRef.current = path;
    return () => {
      if (heldPathRef.current === path) heldPathRef.current = null;
    };
  }, [heldPathRef, path]);
  return markRouteReady;
}

export const useTransitionExit = () => useContext(NavigationContext).exit;

const isDetailPath = (path: string | null) => !!path?.includes(ROUTES.projectDetail);

export function RouteTransitionProvider({ children }: { children: ReactNode }) {
  const [readyPath, setReadyPath] = useState<string | null>(null);
  const [readySeq, setReadySeq] = useState(0);
  const heldPathRef = useRef<string | null>(null);

  const markRouteReady = useCallback((path: string) => {
    setReadyPath(path);
    setReadySeq((seq) => seq + 1);
  }, []);

  const value = useMemo(() => ({ readyPath, readySeq, markRouteReady, heldPathRef }), [readyPath, readySeq, markRouteReady]);

  return <RouteReadyContext.Provider value={value}>{children}</RouteReadyContext.Provider>;
}

export function SwipeTransition({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { readyPath, readySeq, heldPathRef } = useContext(RouteReadyContext);
  const nodeRef = useRef<HTMLDivElement>(null);
  const isProjectDetails = isDetailPath(pathname);
  const enteredPathRef = useRef(pathname);
  const readySeqRef = useRef(readySeq);
  readySeqRef.current = readySeq;
  const readySeqAtEntryRef = useRef(readySeq);
  const { contextSafe } = useGSAP({ scope: nodeRef });

  const entryTlRef = useRef<gsap.core.Timeline | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNavigatingRef = useRef(false);

  const clearReadyTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const killEntryTl = () => {
    if (entryTlRef.current) {
      entryTlRef.current.kill();
      entryTlRef.current = null;
    }
  };

  const exit = contextSafe((href: string) => {
    if (isNavigatingRef.current || href === pathname) return;
    const node = nodeRef.current;
    if (!node) {
      router.push(href);
      return;
    }
    isNavigatingRef.current = true;
    killEntryTl();
    clearReadyTimeout();
    router.prefetch(href); // Prefetch the data from the next page while we animate the page transition.
    gsap.to(node, {
      yPercent: isProjectDetails ? 100 : -100,
      autoAlpha: 0,
      duration: 0.2,
      overwrite: 'auto',
      onComplete: () => router.push(href),
    });
  });

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    gsap.killTweensOf(node);
    killEntryTl();
    clearReadyTimeout();

    // The server already painted the first route in place, so it does not slide in.
    if (enteredPathRef.current === pathname) return;
    enteredPathRef.current = pathname;

    gsap.set(node, { autoAlpha: 0, yPercent: isProjectDetails ? 100 : -100 });

    entryTlRef.current = gsap.timeline({
      paused: true,
      onComplete: () => {
        isNavigatingRef.current = false;
        entryTlRef.current = null;
      },
    }).to(node, { autoAlpha: 1, yPercent: 0, duration: 0.2, ease: 'power2.out' });

    if (heldPathRef.current === pathname) {
      readySeqAtEntryRef.current = readySeqRef.current;
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        const current = entryTlRef.current;
        if (current && current.progress() === 0 && !current.isActive()) {
          current.play(0);
        }
      }, READY_TIMEOUT_MS);
    } else {
      entryTlRef.current.play(0);
    }

    return () => {
      killEntryTl();
      clearReadyTimeout();
    };
  }, [pathname, isProjectDetails, heldPathRef]);

  useEffect(() => {
    if (readyPath !== pathname || readySeq <= readySeqAtEntryRef.current) return;
    const tl = entryTlRef.current;
    if (!tl) return;
    if (tl.isActive() || tl.progress() > 0) return;
    clearReadyTimeout();
    tl.play(0);
  }, [pathname, readyPath, readySeq]);

  return (
    <NavigationContext.Provider value={{ exit }}>
      <div ref={nodeRef} className="swipe-transition-panel">
        {children}
      </div>
    </NavigationContext.Provider>
  );
}
